import OktaAuth from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { Outlet } from '@rocketlane/rocket-router';
import React, { memo, Suspense, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import { PageLoading } from 'components/atoms/loading';
import { TokenErrorStatus } from 'components/organisms/token-error/constant';
import { getCurrentApiHost } from 'config/environment';
import { CONFIG } from 'config/okta';
import { RoutePaths } from 'constant';
import { setQueryClientAction } from 'data/core/actions';
import { queryClient, useAccountMetaQuery } from 'queries';
import { MobileViewOverlay } from 'routes/mobile-view-overlay';
import { getOkta, logger, setOkta, ApiKey } from 'services';
import { authService } from 'services/auth-service';

// Keep TokenErrorStatus as regular import since it's an enum/type

const AccountNotFound = React.lazy(() =>
  import('components/organisms/account').then((d) => ({
    default: d.AccountNotFound,
  })),
);

const TokenError = React.lazy(() =>
  import('components/organisms/token-error/token-error').then((d) => ({
    default: d.TokenError,
  })),
);

export const SetupOkta = memo(() => {
  const dispatch = useDispatch();
  const { pathname } = window.location;
  const [authSetupComplete, setAuthSetupComplete] = useState(false);

  useEffect(() => {
    dispatch(setQueryClientAction(queryClient));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, queryClient]);

  const { data, isLoading } = useAccountMetaQuery({
    onSuccess: async ({ okta: { clientId, issuerDomain } }) => {
      // Guard: only create the OktaAuth instance once. In React 18 StrictMode
      // effects (and their cleanups) run twice, which would call onSuccess again
      // with a fresh OktaAuth object — passing a new reference to <Security>
      // causes it to re-initialize, resetting authState and unmounting the tree.
      if (getOkta() !== null) {
        return;
      }

      // const oktaConfig = CONFIG(issuerDomain, clientId);
      // const authInstance = new OktaAuth(oktaConfig);

      try {
        const response = await fetch(
          `${getCurrentApiHost()}/api/v1/home/init`,
          {
            method: 'GET',
            credentials: 'include',
            headers: ApiKey.header(),
          },
        );

        if (response.status === 200) {
          authService.setAuthState(true);
        } else {
          authService.setAuthState(false);
        }
      } catch (error) {
        logger.error('Init call failed', { error: error as Error });
        authService.setAuthState(false);
      }

      if (clientId && issuerDomain) {
        const oktaConfig = CONFIG(issuerDomain, clientId);
        const authInstance = new OktaAuth(oktaConfig);

        setOkta(authInstance);

        authInstance.authStateManager.updateAuthState();
      }
      setAuthSetupComplete(true);
    },
    onError: (err) => {
      logger.error('Account meta failed', { error: err as Error });
      setAuthSetupComplete(true);
    },
  });

  const oktaAuth = getOkta();
  const isAccountActionPage = pathname === RoutePaths.activate;

  // Stable callback — prevents Security from re-registering on every render
  const stableRestoreOriginalUri = useCallback(() => {}, []);

  const componentToRender = () => {
    if (isLoading || !authSetupComplete) {
      return <PageLoading />;
    }

    if (data) {
      const appContent = (
        <Suspense fallback={<PageLoading />}>
          <Helmet
            titleTemplate={`%s - ${data.company?.companyName}`}
            defaultTitle={data.company?.companyName}
          >
            <link
              rel="icon"
              type="image/png"
              href={data.company?.companyLogoUrl}
            />
            <link
              rel="icon"
              type="image/png"
              sizes="16x16"
              href={data.company?.companyLogoUrl}
            />
          </Helmet>
          <Outlet />
        </Suspense>
      );

      if (oktaAuth) {
        return (
          <Security
            oktaAuth={oktaAuth}
            restoreOriginalUri={stableRestoreOriginalUri}
          >
            {appContent}
          </Security>
        );
      }

      // Cookies flow: no Okta setup needed, render directly
      return appContent;
    }
    if (isAccountActionPage) {
      return (
        <Suspense fallback={<PageLoading />}>
          <TokenError status={TokenErrorStatus.AlreadyCompleted} />
        </Suspense>
      );
    }

    return (
      <Suspense fallback={<PageLoading />}>
        <AccountNotFound />
      </Suspense>
    );
  };

  return <MobileViewOverlay>{componentToRender()}</MobileViewOverlay>;
});

SetupOkta.displayName = 'SetupOkta';
